<template>
  <div
    :id="id"
    ref="container"
    :class="['modal', modalClass, { fade: !noFade }]"
    tabindex="-1"
    role="dialog"
    :aria-hidden="isShown ? 'false' : 'true'"
    @click.self="onBackdropClick"
  >
    <div :class="['modal-dialog', ...dialogClasses]" role="document">
      <div :class="['modal-content', contentClass]">
        <!-- HEADER -->
        <div v-if="!hideHeader" class="modal-header">
          <slot name="modal-header" :close="hide"></slot>
          <button
            v-if="!hasCustomClose && !hideHeaderClose"
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="hide"
          ></button>
        </div>
        <!-- BODY -->
        <div :class="['modal-body', bodyClass]">
          <slot :close="hide"></slot>
        </div>
        <!-- FOOTER -->
        <div v-if="!hideFooter" class="modal-footer">
          <slot name="modal-footer" :close="hide"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";

export default {
  name: "BaseModal",
  inheritAttrs: false,
  // allow using v-model for controlling visibility : OPTIONAL
  model: {
    prop: "visible",
    event: "update:visible",
  },

  props: {
    id: { type: String, default: "" },
    visible: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    modalClass: { type: String, default: "" },
    dialogClass: { type: String, default: "" },
    contentClass: { type: String, default: "" },
    bodyClass: { type: String, default: "" },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noFade: { type: Boolean, default: false },
    backdrop: { type: [Boolean, String], default: true },
    keyboard: { type: Boolean, default: true },
    centered: { type: Boolean, default: false },
    size: { type: String, default: "" },
    scrollable: { type: Boolean, default: false },
    hideHeaderClose: { type: Boolean, default: false },
  },
  data() {
    return { bsInstance: null, isShown: false };
  },
  computed: {
    dialogClasses() {
      const cls = [];
      if (this.dialogClass) cls.push(this.dialogClass);
      if (this.centered) cls.push("modal-dialog-centered");
      if (this.scrollable) cls.push("modal-dialog-scrollable");
      if (this.size) cls.push(`modal-${this.size}`);
      return cls;
    },
    hasCustomClose() {
      return !!(
        this.$slots["modal-header"] || this.$scopedSlots["modal-header"]
      );
    },
  },
  // watch for changes in the "visible" prop to show/hide the modal "OPTIONAL"
  watch: {
    visible(val) {
      if (val) this.show();
      else this.hide();
    },
  },

  mounted() {
    this._ownedBackdrop = null;
    this._suppressHideEmit = false;
    if (!this.$refs.container) return;
    // initialize Bootstrap modal instance
    this.bsInstance = new Modal(this.$refs.container, {
      backdrop: this.noCloseOnBackdrop ? "static" : this.backdrop,
      keyboard: this.keyboard,
      focus: false,
      show: false,
    });
    // when Bootstrap fires its "hidden" event, clean up only what this modal owns
    this.$refs.container.addEventListener("hidden.bs.modal", () => {
      if (this._ownedBackdrop && this._ownedBackdrop.parentNode) {
        this._ownedBackdrop.parentNode.removeChild(this._ownedBackdrop);
      }
      this._ownedBackdrop = null;
      // Do NOT detach the container here — Bootstrap's Modal instance keeps a
      // reference to this element for the lifetime of the instance, and async
      // transition callbacks read `_element.style`. Leaving the node attached
      // (just without `.show`) keeps Bootstrap's invariants intact.
    });
    // sync events. `hide.bs.modal` is cancelable; consumers can call
    //   @hide="(e) => { if (unsaved) e.preventDefault(); }"
    // to abort UI-initiated close. Programmatic hide() emits its own
    // cancelable synthetic event before invoking Bootstrap.
    ["show", "shown", "hide", "hidden"].forEach((evt) => {
      this.$refs.container.addEventListener(`${evt}.bs.modal`, (e) => {
        if (evt === "hide" && this._suppressHideEmit) {
          this._suppressHideEmit = false;
          return;
        }
        this.$emit(evt, e);
        if (evt === "shown") {
          this.isShown = true;
          this.$emit("update:visible", true);
          this.adjustBackdropOpacity();
        }
        if (evt === "hidden") {
          this.isShown = false;
          this.$emit("update:visible", false);
        }
      });
    });

    // auto-show if prop initial true
    if (this.visible) {
      this.show();
    }

    // global bus
    this._onShow = (id) => id === this.id && this.show();
    this._onHide = (id) => id === this.id && this.hide();

    this.$root.$on("base-modal:show", this._onShow);
    this.$root.$on("base-modal:hide", this._onHide);

    // register so $bvModal.show(id) calls fired before this modal mounted
    // get flushed now (A5)
    if (this.id && this.$root._modalBus) {
      this.$root._modalBus._register(this.id);
    }
  },
  beforeDestroy() {
    if (this.id && this.$root._modalBus) {
      this.$root._modalBus._unregister(this.id);
    }

    // Force-finish any in-flight Bootstrap transition before dispose so a
    // queued `_showElement` / `_hideModal` callback doesn't fire later on a
    // nulled `_element`. Removing `.fade` makes transitionend fire
    // synchronously.
    const container = this.$refs.container;
    if (
      this.bsInstance &&
      (this.bsInstance._isTransitioning || this.bsInstance._isShown)
    ) {
      if (container) container.classList.remove("fade");
      try {
        this.bsInstance.hide();
      } catch (_) {
        // instance may already be tearing down
      }
    }

    // if parent v-if'd us away while still open, Bootstrap's dispose()
    // skips the normal hidden.bs.modal cleanup — do it manually (A7)
    if (this._ownedBackdrop && this._ownedBackdrop.parentNode) {
      this._ownedBackdrop.parentNode.removeChild(this._ownedBackdrop);
    }
    this._ownedBackdrop = null;

    const c = this.$refs.container;
    if (c && c.parentNode === document.body) {
      document.body.removeChild(c);
    }

    if (this.bsInstance) {
      this.bsInstance.dispose();
    }

    // restore body if no other modals remain visible
    if (document.querySelectorAll(".modal.show").length === 0) {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("overflow");
    }

    if (this._onShow) this.$root.$off("base-modal:show", this._onShow);
    if (this._onHide) this.$root.$off("base-modal:hide", this._onHide);
  },
  methods: {
    show() {
      // never try to show before the element is even mounted
      const container = this.$refs.container;
      if (
        !container ||
        !this.bsInstance ||
        !this.bsInstance._element ||
        this.bsInstance._isShown
      ) {
        return;
      }

      // append if needed
      if (container.parentNode !== document.body) {
        document.body.appendChild(container);
      }
      // open it
      this.bsInstance.show();

      this.$nextTick(() => {
        this.adjustBackdropOpacity();
        // restack immediately
        const openCount = document.querySelectorAll(".modal.show").length;
        const backdropZ = 1040 + openCount * 20;
        const modalZ = 1050 + openCount * 20;

        container.style.zIndex = modalZ;

        const bd = this.getTopBackdrop();
        if (bd) {
          bd.style.zIndex = backdropZ;
          bd.setAttribute("data-modal-id", this.id || this._uid);
          this._ownedBackdrop = bd;
        }
      });
    },
    getTopBackdrop() {
      const backdrops = document.querySelectorAll(".modal-backdrop");
      return backdrops.length ? backdrops[backdrops.length - 1] : null;
    },
    hide() {
      if (
        !this.bsInstance ||
        !this.bsInstance._element ||
        !this.bsInstance._isShown ||
        this.bsInstance._isTransitioning
      )
        return;

      // allow consumers to cancel programmatic close
      const cancelEvent = {
        defaultPrevented: false,
        preventDefault() {
          this.defaultPrevented = true;
        },
      };
      this.$emit("hide", cancelEvent);
      if (cancelEvent.defaultPrevented) return;

      this._suppressHideEmit = true;
      const bd = this._ownedBackdrop || this.getTopBackdrop();
      if (bd) {
        bd.style.opacity = "0";
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      this.bsInstance.hide();
    },
    onBackdropClick() {
      if (this.noCloseOnBackdrop) return;
      if (this.backdrop) this.hide();
    },
    adjustBackdropOpacity() {
      const bd = this.getTopBackdrop();
      if (bd) {
        bd.style.transition = "opacity 0.3s ease";
        bd.style.opacity = "0.8";
      }
    },
  },
};
</script>

<style scoped>
.modal {
  transition: opacity 0.15s linear;
}
.modal.show {
  display: block;
}
</style>
